import { useQuery, useQueryClient } from '@tanstack/react-query';
import { appelApi } from './api';

/**
 * Contrats d'apprentissage de l'écran `adm-contrats-apprentissage`.
 *
 * L'état du contrat (`etat`), son libellé, son ton et son détail sont
 * **calculés par l'API**, par `situationContrat` dans
 * `backend/src/domain/contrat.ts`. Le front ne les recalcule ni ne les
 * renomme : c'est ce qui garantit qu'un contrat rompu ne peut pas s'afficher
 * « Actif », ni un contrat au CERFA généré « À venir », parce qu'un écran
 * aurait gardé sa propre table de libellés.
 */
export type EtatContrat = 'A_VALIDER' | 'REFUSE' | 'VALIDE' | 'ACTIF' | 'TERMINE' | 'ROMPU';

/** Situation d'un contrat telle que l'API la rend, prête à afficher. */
export interface SituationContrat {
  etat: EtatContrat;
  libelle: string;
  ton: string;
  detail: string | null;
}

export interface RuptureContrat {
  /** Vide tant que la date n'est pas arrêtée. */
  dateRupture: string | null;
  motif: string;
  formationMaintenue: boolean;
  commentaire: string | null;
  /** Date d'enregistrement de la déclaration, distincte de la date de rupture. */
  enregistreeLe: string;
  saisiePar: string | null;
  /** Document de rupture produit à la déclaration, s'il a pu l'être. */
  documentId: string | null;
}

export interface ContratLigne {
  /** Identifiant de l'immersion : c'est lui que portent les actions CERFA. */
  id: string;
  candidat: string;
  apprenantId: string;
  siteCode: string;
  entreprise: string;
  entrepriseId: string;
  formation: string | null;
  /** Vide pour un positionnement sans immersion. */
  dateFinImmersion: string | null;
  sansImmersion: boolean;
  decisionContrat: string | null;
  statutContrat: string | null;
  etat: EtatContrat;
  etatLibelle: string;
  etatTon: string;
  /** Seconde ligne : étape du document et date de début. */
  etatDetail: string | null;
  contratId: string | null;
  dateDebut: string | null;
  dateFin: string | null;
  dateSignature: string | null;
  saisieCerfaLe: string | null;
  /** CERFA généré pour ce contrat, à relire sans passer par l'écran de saisie. */
  documentCerfaId: string | null;
  rupture: RuptureContrat | null;
}

/** Date civile réunionnaise, ou tiret quand la donnée manque. */
export function jourContrat(valeur: string | null | undefined): string {
  if (!valeur) return '—';
  const date = new Date(valeur);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' });
}

/** Mention d'un contrat rompu, avec sa date quand elle est arrêtée. */
export function mentionRupture(dateRupture: string | null): string {
  return dateRupture ? `Rompu le ${jourContrat(dateRupture)}` : 'Rompu · date à préciser';
}

export function useContrats(site: string) {
  return useQuery({
    queryKey: ['contrats', site],
    queryFn: () =>
      appelApi<ContratLigne[]>(`/contrats?${new URLSearchParams({ site }).toString()}`),
  });
}

/**
 * Rafraîchit la liste après une validation, une correction ou un refus.
 *
 * Y compris quand la liste n'est pas affichée : la page de saisie la quitte, et
 * un simple marquage « périmé » la faisait réapparaître un instant avec
 * l'ancien état au retour.
 */
export function useRafraichirContrats(): () => Promise<void> {
  const client = useQueryClient();
  return () => client.invalidateQueries({ queryKey: ['contrats'], refetchType: 'all' });
}
